<template>
    <CieloLayout layout-version="v1">
        <LoadingScreen v-if="!hasOrderStatusData" :hide-header="true" />
        <div v-else class="order-status" :class="[this.status, { 'order-status--dcc': showDccLayout }]">
            <!-- BBPay's order status header -->
            <BBPayOrderStatusHeader
                v-if="isBBPayPayment"
                :step-index="stepperConfig.activeStep"
                :step-labels="stepLabels"
                :stepper-config="stepperConfig"
                :merchant-name="merchantName"
                :highlight-email="highlightEmail"
                :status="status"
                class="spacing-b-6"
            />
            <OrderStatusHeader
                v-else
                :status="this.status"
                :merchant-name="this.merchantName"
                :id-pay-redirect="this.idPayRedirect"
            />
            <CieloButton
                v-if="this.status === 'idpay_pending' && this.idPayLink && !this.idPayRedirect"
                class="confirm-cta"
                @click="handleIdPayPendingCTA"
            >
                <span v-text="$ml.get('confirmIdentity')" />
            </CieloButton>
            <OrderStatusOverview :status="this.status" />
            <CieloButton v-if="this.status === 'denied' && this.shortUrl" class="retry-cta" @click="handleRetryCTA">
                <span v-text="$ml.get('retry')" />
            </CieloButton>
        </div>
        <CieloFooter v-if="isBBPayPayment" />
    </CieloLayout>
</template>

<script>
import { logException, logEvent } from '@/helpers/cieloLogger';
import { mapGetters } from 'vuex';
import LoadingScreen from '@/components/LoadingScreen';
import OrderStatusHeader from '@/components/OrderStatus/OrderStatusHeader';
import OrderStatusOverview from '@/components/OrderStatus/OrderStatusOverview';
import CieloFooter from '@/components/template/CieloFooter.vue';
import BBPayOrderStatusHeader from '@/components/OrderStatus/BBPayOrderStatusHeader.vue';
import { getStepperConfig } from '@/helpers/orderStatusStepperHelper';
import PaymentNotificator from '@/services/PaymentNotificator';
import OrderStatusHeaderConfig from '@/models/OrderStatusHeaderConfig';

const CieloButton = () =>
	import(
		/* webpackChunkName: "cielo-button" */
		'@/components/template/CieloButton'
	);
const CieloLayout = () =>
	import(
    /* webpackChunkName: "cielo-layout" */ '@/components/layout/CieloLayout'
	);

export default {
	components: {
		CieloLayout,
		LoadingScreen,
		OrderStatusHeader,
		OrderStatusOverview,
		CieloButton,
		CieloFooter,
		BBPayOrderStatusHeader,
	},

	data() {
		return {
			pollingInterval: null,
			incrementPollingFrequency: 10000, // 10 seconds in milliseconds
			currentPollingFrequency: 5000, // Start with the initial frequency
		};
	},


	async mounted() {
		await this.fetchOrderStatus(this.orderId);
		if (this.idPayRedirect && this.status === 'idpay_pending') {
			this.startPolling();
		}
		// Quando houver SignalR para outros meios, ajustar as regras abaixo.
		const shouldListenToRealtime =
			(this.withPolling && this.isOpenFinancePending) ||
			(this.isBBPayPayment && this.isPending);

		if (shouldListenToRealtime) {
			const urlFromCheckoutUI = this.isBBPayPayment;
			try {
				PaymentNotificator.CreateConnection(urlFromCheckoutUI);
				await this.$store.dispatch('orderStatus/listenToRealTimeNotification');
			} catch (error) {
				logException(error);
			}
		}
	},


	beforeDestroy() {
		if (this.pollingInterval) {
			clearInterval(this.pollingInterval);
		}
	},

	computed: {
		...mapGetters({
			hasOrderStatusData: 'orderStatus/hasOrderStatusData',
			status: 'orderStatus/getStatus',
			merchantName: 'orderStatus/getMerchantName',
			shortUrl: 'orderStatus/getShortUrl',
			idPayLink: 'orderStatus/getIdPayLink',
			hasPreorder: 'preorder/hasPreorder',
			isBBPayPayment: 'bBPayPayment/getIsBBPayPayment',
			customerEmail: 'orderStatus/getCustomerEmail',
			dcc: 'orderStatus/getDcc'
		}),
		/**
		 * Mesma condição usada por OrderStatusOverview para exibir a seção do DCC: a flag do
		 * status decide se o comprovante *pode* ter conversão, a presença do dado decide se
		 * este pedido *de fato* teve adesão. Vive aqui também porque o cabeçalho (os-header)
		 * é irmão do overview, fora do alcance do CSS scoped dele.
		 */
		showDccLayout() {
			const config = OrderStatusHeaderConfig[this.status] || {};
			return !!(config.showDccSummary && this.dcc);
		},
		orderId() {
			return this.$route.query.orderId;
		},
		idPayRedirect() {
			return this.$route.query.idPayRedirect;
		},
		withPolling() {
			return this.$route.query.polling;
		},
		isOpenFinancePending() {
			return this.status === 'openfinancepending';
		},
		isPending() {
			return this.status === 'pending';
		},
		stepLabels() {
			return [
				'stepperText1',
				'stepperText2',
				'stepperText3',
			];
		},
		stepperConfig() {
            return getStepperConfig(this.status);
        },
		highlightEmail() {
			return this.customerEmail || '';
		},
	},

	methods: {
		async fetchOrderStatus(orderId) {
			if (!orderId)
				return this.$store.dispatch('pipeline/pushTo', 'InvalidUrl');

			try {
				await this.$store.dispatch('orderStatus/fetchOrderStatus', orderId);
			} catch (error) {
				logException(error);

				if (this.hasPreorder) {
					return this.$store.dispatch('pipeline/pushTo', 'PaymentSuccess');
				} else {
					return this.$store.dispatch('pipeline/pushTo', 'InvalidOrder');
				}
			}
		},

		localizeOrderDate(orderDate, currentLocale) {
			const pattern = /(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2}):(\d{2})/;
			let date = new Date(orderDate.replace(pattern, '$3-$2-$1T$4:$5:$6Z'));

			return date.toLocaleString(currentLocale, {
				timeZone: 'UTC',
				dateStyle: 'medium',
				timeStyle: 'medium',
			});
		},

		handleRetryCTA() {
			window.location.href = this.shortUrl;
		},

		handleIdPayPendingCTA() {
			window.open(this.idPayLink, '_blank');

			logEvent({
				category: 'checkout',
				action: 'CTA_IdPayPendente',
				label: `clique no botão exibindo o OrderStatus: ${this.Status}`,
			});
		},

		startPolling() {
			this.pollingInterval = setInterval(this.checkOrderStatus, this.currentPollingFrequency);
		},

		async checkOrderStatus() {
			try {
				await this.$store.dispatch('orderStatus/pollingFetchOrderStatus', this.orderId);
				// Increase the polling interval by 10 seconds
				clearInterval(this.pollingInterval);
				this.currentPollingFrequency += this.incrementPollingFrequency;
				this.startPolling();
			} catch (error) {
				logException(error);
				// Retry with the same interval in case of an error
				this.startPolling();
			}
		},
	},
};
</script>

<style lang="scss" scoped>
.order-status {
	&__bbpay-title {
		font-size: 24px;
		font-weight: 700;
		color: #2F363E;
	}
	&.denied {
		@media screen and (max-width: 1024px) {
			display: flex;
			flex-direction: column;
			flex: 1 0;
		}

		.os-header {
			flex: 1 0;
		}

		.retry-cta {
			@media screen and (min-width: 1025px) {
				min-width: 0;
			}
		}
	}

	&.idpay_pending {
		.confirm-cta {
			margin-bottom: 20px;

			@media screen and (min-width: 1025px) {
				min-width: 0;
			}
		}
	}

	// Cabeçalho (os-header) e resumo (os-overview) são irmãos aqui, por isso a largura do
	// cenário de DCC é limitada neste nível — dentro de OrderStatusOverview o CSS scoped
	// não alcançaria o cabeçalho.
	&--dcc {
		max-width: 580px;
		margin: 0 auto;
	}
}

strong {
	font-weight: 600;
	color: #454F5C;
}

.loading-container {
	position: relative;
	width: 100px;
	height: 100px;
	display: flex;
	align-items: center;
	justify-content: center;
}

.circle {
	position: relative;
	font-size: 20px;
	font-weight: 700;
	line-height: 28px;
	color: #2F363E;
}


.loading-circle {
	position: absolute;
	width: 100px;
	height: 100px;
	border: 6px solid #DBE0E5;
	border-top: 6px solid #0774E7;
	border-radius: 50%;
	animation: spin 2s linear infinite;

	@keyframes spin {
		0% {
			transform: rotate(0deg);
		}

		100% {
			transform: rotate(360deg);
		}
	}
}
</style>
